import axios from 'axios';
import type { AxiosInstance } from 'axios';
import React from 'react';
import { useAuth } from '@auth/oauth/AuthProvider';
import { createAuthRequestInterceptor } from './authRequestInterceptor';

export type UseAuthClientOptions = {
  /**
   * An Axios instance created outside this hook (e.g. a module-scoped
   * singleton wired into Redux saga context, or a client shared across
   * multiple hooks) that should receive the shared auth interceptor instead
   * of a freshly created one.
   *
   * When provided, `baseURL`/`timeout`/`headers`/`platform`/`withCredentials`
   * are ignored — this hook only registers the interceptor and returns the
   * same instance.
   */
  existingClient?: AxiosInstance;
  /**
   * Base URL for the Axios instance (e.g. API gateway URL). Required unless
   * `existingClient` is provided.
   */
  baseURL?: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Additional default headers to include on every request */
  headers?: Record<string, string>;
  /**
   * Platform identifier sent via Peloton-Platform header.
   * Defaults to 'web'.
   */
  platform?: string;
  /**
   * Whether to send cookies cross-origin (axios `withCredentials`).
   *
   * Defaults to `false` — the unified auth model uses Bearer tokens (Auth0 or
   * checkout-token) and does not rely on legacy session cookies.
   */
  withCredentials?: boolean;

  /**
   * When `false`, skips registering the auth interceptor entirely (the
   * client is still created/returned, just without auth attached).
   *
   * Exists so callers that must call this hook unconditionally — e.g. to
   * satisfy the rules of hooks while a feature flag decides whether this
   * hook or a legacy interceptor should own auth-attaching for a given
   * render — can flip attachment on/off without conditionally calling the
   * hook itself. Defaults to `true`.
   */
  enabled?: boolean;
};

/**
 * Creates a memoised Axios client that attaches auth credentials using a
 * three-tier fallback strategy per the Unified Auth design doc:
 *
 *  1. **Auth0 access token** (Bearer header) — preferred for authenticated users.
 *  2. **Checkout access token** (Bearer header) — fallback for guest checkout flows
 *     where the user has a JWT issued by the checkout service but no Auth0 session.
 *  3. **withCredentials** (opt-in) — only when explicitly enabled; sends the legacy
 *     session cookie for endpoints not yet migrated to Bearer token auth.
 *
 * The interceptor runs on every request and always resolves to the highest-priority
 * token available at call time. This means if a user logs in mid-session, subsequent
 * requests will automatically pick up the Auth0 token.
 */

export const useAuthClient = ({
  existingClient,
  baseURL,
  timeout,
  headers = {},
  platform = 'web',
  withCredentials = false,
  enabled = true,
}: UseAuthClientOptions): AxiosInstance => {
  const { getAccessToken } = useAuth();

  // Serialise headers to a stable string so that object identity changes
  // (e.g. inline literals) don't bust the memo unnecessarily, while genuine
  // value changes (new locale header, tracing header) still trigger a rebuild.
  const headersSerialized = JSON.stringify(headers);

  const client = React.useMemo(() => {
    if (existingClient) {
      return existingClient;
    }

    const parsedHeaders: Record<string, string> = JSON.parse(headersSerialized);
    const instance = axios.create({
      baseURL,
      responseType: 'json',
      withCredentials,
      timeout,
      headers: {
        'X-Requested-With': 'XmlHttpRequest',
        'Peloton-Platform': platform,
        ...parsedHeaders,
      },
    });

    return instance;
  }, [existingClient, baseURL, timeout, platform, withCredentials, headersSerialized]);

  // We store the interceptor ID so we can eject & re-register when getAccessToken changes
  const interceptorIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    // Eject previous interceptor if present
    if (interceptorIdRef.current !== null) {
      client.interceptors.request.eject(interceptorIdRef.current);
      interceptorIdRef.current = null;
    }

    if (!enabled) {
      return;
    }

    interceptorIdRef.current = client.interceptors.request.use(
      createAuthRequestInterceptor(getAccessToken),
    );

    return () => {
      if (interceptorIdRef.current !== null) {
        client.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, [client, getAccessToken, enabled]);

  return client;
};
